import {
  useEffect,
  useRef,
  useState,
  useMemo
} from "react";

import SignatureCanvas
  from "react-signature-canvas";

import { consentTemplates }
  from "./consentTemplates";

import { db }
  from "./firebase";

import jsPDF
  from "jspdf";

import html2canvas
  from "html2canvas";

import { useClinic }
  from "./context/ClinicContext";

import FileViewerModal
  from "./FileViewerModal";

import {
  collection,
  doc,
  addDoc,
  query,
  where,
  onSnapshot,
  deleteDoc
} from "./access/firestore";

import {
  Check,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  FileCheck2,
  FileText,
  History,
  PenLine,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserRound,
  X
} from "lucide-react";

export default function ConsentimientosPanel({
  paciente
}) {

  const {
    clinicId,
    clinicData
  } = useClinic();

  const [
    tipo,
    setTipo
  ] = useState(
    "exodoncia"
  );

  const [
    texto,
    setTexto
  ] = useState("");

  const [
    historial,
    setHistorial
  ] = useState([]);

  const [
    configuracionesConsentimientos,
    setConfiguracionesConsentimientos
  ] = useState([]);

  const [
    toast,
    setToast
    ] = useState("");

  const [
    modoHistorial,
    setModoHistorial
    ] = useState(false);

  const [
    consentimientoSeleccionado,
    setConsentimientoSeleccionado
  ] = useState(null);

  const [
    modalVisible,
    setModalVisible
  ] = useState(false);

  const [
    visorConsentimiento,
    setVisorConsentimiento
  ] = useState(null);

  const [
    consentimientoAbriendoId,
    setConsentimientoAbriendoId
  ] = useState(null);

  const [
    consentimientoAEliminar,
    setConsentimientoAEliminar
  ] = useState(null);

  const [
    guardando,
    setGuardando
  ] = useState(false);

  const sigPacienteRef =
    useRef();

  const sigDoctorRef =
    useRef();

  const firmaPacienteContainerRef =
    useRef();

  const [anchoVentana, setAnchoVentana] =
    useState(window.innerWidth);

  useEffect(() => {
    const actualizarAncho = () => {
      setAnchoVentana(window.innerWidth);
    };

    window.addEventListener(
      "resize",
      actualizarAncho
    );

    return () => {
      window.removeEventListener(
        "resize",
        actualizarAncho
      );
    };
  }, []);

  const vistaUnaColumna =
    anchoVentana < 1050;

  const pdfRef =
    useRef();

  const visorPdfRef =
    useRef();

  const toastTimeout =
  useRef(null);

  const mostrarToast = (
    mensaje
  ) => {

    setToast(mensaje);

    clearTimeout(
      toastTimeout.current
    );

    toastTimeout.current =
      setTimeout(() => {

        setToast("");

      }, 3000);
  };

  useEffect(() => {

    return () => {

      clearTimeout(
        toastTimeout.current
      );
    };

  }, []);

  const {
      currentUser
  } = useClinic();

  useEffect(() => {
    if (!clinicId) return;

    const ref = collection(
      db,
      "clinics",
      clinicId,
      "consentimientosConfiguracion"
    );

    return onSnapshot(
      ref,
      snapshot => {
        setConfiguracionesConsentimientos(
          snapshot.docs.map(configuracion => ({
            id: configuracion.id,
            ...configuracion.data()
          }))
        );
      },
      error => {
        console.error("Error cargando configuración de consentimientos:", error);
        mostrarToast("No fue posible actualizar los tipos de consentimiento");
      }
    );
  }, [clinicId]);

  const doctor = useMemo(
    () => ({
      nombre:
        currentUser?.nombre ||
        "Sin configurar",

      cedula:
        currentUser?.cedula ||
        "Sin cédula",

      especialidad:
        currentUser?.especialidad ||
        "Cirujano Dentista",

      universidad:
        currentUser?.universidad ||
        ""
    }),
    [currentUser]
  );

  const consentimientosActivos = useMemo(() => [
    ...Object.keys(consentTemplates)
      .filter(t => {

        const configuracion =
          configuracionesConsentimientos.find(
            c => c.id === t
          );

        return configuracion?.activo !== false;
      })
      .map(t => ({
        id: t,
        nombre: {
          exodoncia:
            "Exodoncia",

          endodoncia:
            "Endodoncia",

          ortodoncia:
            "Ortodoncia",

          implantes:
            "Implantes Dentales",

          operatoria:
            "Operatoria Dental",

          periodoncia:
            "Tratamientos de Periodoncia",

          odontologiaIntegral:
            "Odontología Integral",

          prostodoncia:
            "Prostodoncia",

          profilaxis:
            "Profilaxis Dental",

          radiografias:
            "Radiografías Dentales",

          anestesia:
            "Anestesia Local"

        }[t] || t,

        origen: "vexadent",
        version: 1
      })),

    ...configuracionesConsentimientos
      .filter(
        c =>
          c.origen === "custom" &&
          c.activo !== false
      )
      .map(c => ({
        id: c.id,
        nombre: c.nombre,
        origen: "custom",
        version: c.version || 1
      }))
  ], [configuracionesConsentimientos]);

  const tipoActual = useMemo(
    () => consentimientosActivos.find(consentimiento => consentimiento.id === tipo),
    [consentimientosActivos, tipo]
  );

  const ultimoConsentimiento = historial[0];

  useEffect(() => {
    /*
    * Si el consentimiento actualmente seleccionado
    * dejó de estar activo, seleccionamos automáticamente
    * el primero que sí esté disponible.
    */
    const tipoSigueActivo =
      consentimientosActivos.some(
        consentimiento =>
          consentimiento.id === tipo
      );

    if (
      !tipoSigueActivo &&
      consentimientosActivos.length > 0
    ) {
      setTipo(
        consentimientosActivos[0].id
      );
    }

    /*
    * Si no queda ningún consentimiento activo,
    * limpiamos la selección.
    */
    if (
      !tipoSigueActivo &&
      consentimientosActivos.length === 0
    ) {
      setTipo("");
    }

  }, [
    consentimientosActivos,
    tipo
  ]);

  const renderConsentText = contenido => {
    if (!contenido) {
      return null;
    }

    const esHtml =
      /<([a-z][\s\S]*?)>/i.test(contenido);

    if (esHtml) {
      return (
        <div
          className="consentimiento-documento-html"
          dangerouslySetInnerHTML={{
            __html: contenido
          }}
        />
      );
    }

    return contenido
      .split("\n")
      .filter(line => line.trim() !== "")
      .map((line, index) => {
        const trimmed = line.trim();

        if (trimmed.startsWith("-")) {
          return (
            <li
              key={index}
              style={{
                marginBottom: 12,
                marginLeft: 24,
                lineHeight: 1.9,
                fontSize: "inherit"
              }}
            >
              {trimmed.replace("-", "").trim()}
            </li>
          );
        }

        const esTitulo =
          trimmed === trimmed.toUpperCase() &&
          trimmed.length > 8;

        if (esTitulo) {
          return (
            <h3
              key={index}
              style={{
                marginTop: 38,
                marginBottom: 18,
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: 0.5,
                color: "#111827"
              }}
            >
              {trimmed}
            </h3>
          );
        }

        return (
          <p
            key={index}
            style={{
              textAlign: "justify",
              lineHeight: 2.1,
              marginBottom: 24,
              fontSize: "inherit"
            }}
          >
            {trimmed}
          </p>
        );
      });
  };

  // =========================
  // GENERAR CONSENTIMIENTO
  // =========================

  useEffect(() => {
    if (modoHistorial) return;

    /*
    * Buscamos exactamente el consentimiento
    * que está seleccionado en el selector.
    */
    const consentimientoSeleccionado =
      consentimientosActivos.find(
        consentimiento =>
          consentimiento.id === tipo
      );

    /*
    * Si el consentimiento seleccionado es personalizado,
    * usamos directamente su plantilla de Firestore.
    */
    if (
      consentimientoSeleccionado?.origen ===
      "custom"
    ) {
      const configuracionPersonalizada =
        configuracionesConsentimientos.find(
          configuracion =>
            configuracion.id === tipo &&
            configuracion.origen === "custom"
        );

      if (
        configuracionPersonalizada?.contenidoHtml ||
        configuracionPersonalizada?.textoPlantilla
      ) {
        const textoGenerado =
          configuracionPersonalizada.contenidoHtml ||
          configuracionPersonalizada.textoPlantilla;

        const textoConDatos =
          textoGenerado
            .replace(
              /\{\{paciente\}\}/g,
              `${paciente?.nombre || ""} ${paciente?.apellidos || ""}`.trim()
            )
            .replace(
              /\{\{doctor\}\}/g,
              doctor?.nombre || "Sin configurar"
            );

        setTexto(textoConDatos);
      } else {
        setTexto("");
      }

      return;
    }

    /*
    * Si es un consentimiento de VexaDent,
    * usamos su plantilla predeterminada.
    */
    if (
      consentimientoSeleccionado?.origen ===
      "vexadent"
    ) {
      const template =
        consentTemplates[tipo];

      if (template) {
        const generado = template({
          paciente,
          doctor
        });

        setTexto(generado);
        return;
      }
    }

    setTexto("");

  }, [
    tipo,
    paciente,
    doctor,
    modoHistorial,
    configuracionesConsentimientos,
    consentimientosActivos
  ]);

  // =========================
  // HISTORIAL DE CONSENTIMIENTOS
  // =========================

  useEffect(() => {

    if (
      !paciente?.id ||
      !clinicId
    ) {
      setHistorial([]);
      return;
    }

    const q = query(
      collection(
        db,
        "clinics",
        clinicId,
        "consentimientos"
      ),
      where(
        "pacienteId",
        "==",
        paciente.id
      )
    );

    return onSnapshot(
      q,

      snap => {

        const documentos =
          snap.docs.map(d => ({
            id: d.id,
            ...d.data()
          }));

        /*
        * Ordenamos en el cliente para no depender
        * de un índice compuesto de Firestore.
        */
        documentos.sort((a, b) => {

          const fechaA =
            a.fecha?.seconds
              ? a.fecha.seconds * 1000
              : new Date(a.fecha || 0).getTime();

          const fechaB =
            b.fecha?.seconds
              ? b.fecha.seconds * 1000
              : new Date(b.fecha || 0).getTime();

          return fechaB - fechaA;

        });

        setHistorial(documentos);

      },

      error => {

        console.error(
          "Error cargando historial de consentimientos:",
          error
        );

        mostrarToast(
          "❌ No fue posible cargar el historial de consentimientos"
        );

      }
    );

  }, [
    paciente?.id,
    clinicId
  ]);

  useEffect(() => {

    const ajustarCanvasFirma = () => {

      const canvas =
        sigPacienteRef.current?.getCanvas();

      const container =
        firmaPacienteContainerRef.current;

      if (!canvas || !container) return;

      const ancho =
        Math.floor(container.clientWidth);

      const alto = 220;

      if (
        canvas.width === ancho &&
        canvas.height === alto
      ) {
        return;
      }

      const datos =
        sigPacienteRef.current?.toData() || [];

      canvas.width = ancho;
      canvas.height = alto;

      if (datos.length > 0) {
        sigPacienteRef.current?.fromData(datos);
      }

    };

    ajustarCanvasFirma();

    const observer =
      new ResizeObserver(
        ajustarCanvasFirma
      );

    if (
      firmaPacienteContainerRef.current
    ) {
      observer.observe(
        firmaPacienteContainerRef.current
      );
    }

    window.addEventListener(
      "resize",
      ajustarCanvasFirma
    );

    return () => {

      observer.disconnect();

      window.removeEventListener(
        "resize",
        ajustarCanvasFirma
      );

    };

  }, []);

  // =========================
  // LIMPIAR FIRMAS
  // =========================

  const limpiarFirmas = () => {
    sigPacienteRef.current?.clear();
    sigDoctorRef.current?.clear();
  };

  // =========================
  // ELIMINAR CONSENTIMIENTO
  // =========================

  const eliminarConsentimiento = async () => {

    if (
      !clinicId ||
      !consentimientoAEliminar?.id
    ) {
      return;
    }

    try {

      await deleteDoc(
        doc(
          db,
          "clinics",
          clinicId,
          "consentimientos",
          consentimientoAEliminar.id
        )
      );

      /*
      * Quitamos inmediatamente el documento
      * de la interfaz.
      */
      setHistorial(prev =>
        prev.filter(
          consentimiento =>
            consentimiento.id !==
            consentimientoAEliminar.id
        )
      );

      /*
      * Cerramos la confirmación.
      */
      setConsentimientoAEliminar(null);

      /*
      * Si el consentimiento eliminado estaba
      * abierto en el visor, también lo cerramos.
      */
      setConsentimientoSeleccionado(null);
      setModalVisible(false);

      mostrarToast(
        "✅ Consentimiento eliminado correctamente"
      );

    } catch (error) {

      console.error(
        "Error eliminando consentimiento:",
        error
      );

      mostrarToast(
        "❌ No fue posible eliminar el consentimiento"
      );

    }
  };

  // =========================
  // GUARDAR
  // =========================

  const guardar = async () => {

    if (guardando) {
      return;
    }

    if (!clinicId) {

      setToast(
        "⚠️ Clínica no válida"
      );

      return;
    }

    if (
      sigPacienteRef.current?.isEmpty()
    ) {

      mostrarToast(
        "⚠️ Firma del paciente requerida"
      );

      return;
    }

    if (
      sigDoctorRef.current?.isEmpty()
    ) {
      mostrarToast(
        "⚠️ Firma del odontólogo requerida"
      );

      return;
    }

    const firmaPaciente =
        sigPacienteRef.current
            .getCanvas()
            .toDataURL(
            "image/png"
        );

    const firmaDoctor =
        sigDoctorRef.current
            ?.getCanvas()
            .toDataURL(
            "image/png"
        ) || "";

    const fechaFirma = new Date();

    const folio = `CONS-${
      fechaFirma.getFullYear()
    }-${
      Date.now()
    }`;

    const consentimiento = {

      folio,

      clinicId,

      consentimientoId:
        tipo,

      origen:
        consentimientosActivos.find(
          consentimiento =>
            consentimiento.id === tipo
        )?.origen || "vexadent",

      version:
        consentimientosActivos.find(
          consentimiento =>
            consentimiento.id === tipo
        )?.version || 1,

      pacienteSnapshot: {
        id:
          paciente?.id || "",
        nombre:
          paciente?.nombre || "",
        apellidos:
          paciente?.apellidos || "",
        fechaNacimiento:
          paciente?.fechaNacimiento || "",
        telefono:
          paciente?.telefono || "",
        email:
          paciente?.email || ""
      },

      expedienteLegal: {

      creadoPor:
        doctor.nombre,

      fechaCreacion:
        fechaFirma,

      versionDocumento:
        consentimientosActivos.find(
          consentimiento =>
            consentimiento.id === tipo
        )?.version || 1,

      estado:
        "firmado",

      validado:
        true

    },

      pacienteId:
        paciente.id,

      pacienteNombre:
        `${paciente?.nombre || ""} ${paciente?.apellidos || ""}`.trim(),

      tipo,

      nombreConsentimiento:
        consentimientosActivos.find(
          consentimiento =>
            consentimiento.id === tipo
        )?.nombre || tipo,

      texto,

      firmaPaciente,

      firmaDoctor,

      doctor,

      doctorSnapshot: {
        nombre:
          doctor.nombre,
        cedula:
          doctor.cedula,
        especialidad:
          doctor.especialidad,
        universidad:
          doctor.universidad
      },

      clinicSnapshot: {
        nombreClinica:
          clinicData?.nombreClinica || "",
        telefono:
          clinicData?.telefono || "",
        email:
          clinicData?.email || "",
        RFC:
          clinicData?.RFC || "",
        direccion:
          clinicData?.direccion || {},
        logoURL:
          clinicData?.logoURL || "",
        logoBase64:
          clinicData?.logoBase64 || ""
      },

      fecha:
        fechaFirma

    };

    setGuardando(true);

    try {

        await addDoc(

          collection(
            db,
            "clinics",
            clinicId,
            "consentimientos"
          ),

          consentimiento
        );

        mostrarToast(
          "✅ Consentimiento guardado correctamente"
        );

        limpiarFirmas();

        } catch (err) {

        console.error(err);

        mostrarToast(
          "❌ Error guardando consentimiento"
        );
    } finally {
      setGuardando(false);
    }
  };

        const descargarPDF = async () => {

          if (!pdfRef.current)
            return;

          try {

            mostrarToast(
              "Generando PDF..."
            );

            // =========================
            // GUARDAR ESTILOS
            // =========================

            const originalMaxHeight =
              pdfRef.current.style.maxHeight;

            const originalOverflow =
              pdfRef.current.style.overflowY;

            // =========================
            // EXPANDIR CONTENIDO
            // =========================

            pdfRef.current.style.maxHeight =
              "none";

            pdfRef.current.style.overflowY =
              "visible";

            // =========================
            // ESPERAR RENDER
            // =========================

            await new Promise(resolve =>
              setTimeout(resolve, 300)
            );

            // =========================
            // CAPTURA
            // =========================

            const canvas =
              await html2canvas(
                pdfRef.current,
                {

                  scale: 2,

                  useCORS: true,

                  backgroundColor:
                    "#ffffff",

                  scrollY:
                    -window.scrollY

                }
              );

            // =========================
            // RESTAURAR UI
            // =========================

            pdfRef.current.style.maxHeight =
              originalMaxHeight;

            pdfRef.current.style.overflowY =
              originalOverflow;

            // =========================
            // PDF
            // =========================

            const imgData =
              canvas.toDataURL(
                "image/png"
              );

            const pdf =
              new jsPDF(
                "p",
                "mm",
                "a4"
              );

            const pdfWidth =
              pdf.internal
                .pageSize
                .getWidth();

            const pageHeight =
              pdf.internal
                .pageSize
                .getHeight();

            const imgWidth =
              pdfWidth - 20;

            const imgHeight =
              (
                canvas.height *
                imgWidth
              ) / canvas.width;

            let heightLeft =
              imgHeight;

            let position = 0;

            // PRIMERA PAGINA

            pdf.addImage(

              imgData,

              "PNG",

              10,

              10,

              imgWidth,

              imgHeight

            );

            heightLeft -= pageHeight;

            // PAGINAS EXTRA

            while (heightLeft > 0) {

              position =
                -(imgHeight - heightLeft);

              pdf.addPage();

              pdf.addImage(

                imgData,

                "PNG",

                10,

                position,

                imgWidth,

                imgHeight

              );

              heightLeft -= pageHeight;
            }

            // =========================
            // GUARDAR
            // =========================

            pdf.save(

              `Consentimiento_${consentimientoSeleccionado.tipo}_${paciente?.nombre || "Paciente"}.pdf`

            );

            mostrarToast(
              "✅ PDF generado correctamente"
            );

          } catch (err) {

            console.error(err);

            mostrarToast(
              "❌ Error generando PDF"
            );
          }
        };

        const abrirVisorConsentimiento = async (
          consentimiento
        ) => {

          setConsentimientoAbriendoId(
            consentimiento.id
          );

          try {
            if (!visorPdfRef.current) {
              mostrarToast(
                "❌ No fue posible generar el documento"
              );
              return;
            }

            setConsentimientoSeleccionado(
              consentimiento
            );

            /*
            * Esperamos a que React pinte
            * el consentimiento seleccionado.
            */
            await new Promise(resolve =>
              setTimeout(resolve, 100)
            );

            await new Promise(resolve =>
              setTimeout(resolve, 300)
            );

            const canvas =
              await html2canvas(
                visorPdfRef.current,
                {
                  scale: 2,
                  useCORS: true,
                  backgroundColor:
                    "#ffffff",
                  scrollY:
                    -window.scrollY
                }
              );

            const pdf = new jsPDF("p", "mm", "a4");
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const margin = 10;

            const imgWidth = pdfWidth - margin * 2;
            const scale =
              imgWidth / canvas.width;

            const pagePixelHeight =
              Math.floor(
                (pdfHeight - margin * 2) / scale
              );

            let sourceY = 0;

            while (sourceY < canvas.height) {
              let targetY =
                Math.min(
                  sourceY + pagePixelHeight,
                  canvas.height
                );

              if (targetY < canvas.height) {
                const searchRange = 180;

                const start =
                  Math.max(
                    sourceY + 100,
                    targetY - searchRange
                  );

                const end =
                  Math.min(
                    canvas.height - 1,
                    targetY + searchRange
                  );

                let bestRow = targetY;
                let bestScore = Infinity;

                const ctx =
                  canvas.getContext("2d");

                const width = canvas.width;

                for (
                  let y = start;
                  y <= end;
                  y += 2
                ) {
                  const row =
                    ctx.getImageData(
                      0,
                      y,
                      width,
                      1
                    ).data;

                  let nonWhite = 0;

                  for (
                    let x = 0;
                    x < width;
                    x += 8
                  ) {
                    const i = x * 4;

                    if (
                      row[i] < 245 ||
                      row[i + 1] < 245 ||
                      row[i + 2] < 245
                    ) {
                      nonWhite++;
                    }
                  }

                  if (nonWhite < bestScore) {
                    bestScore = nonWhite;
                    bestRow = y;
                  }
                }

                targetY = bestRow;
              }

              const sliceHeight =
                targetY - sourceY;

              const pageCanvas =
                document.createElement("canvas");

              pageCanvas.width = canvas.width;
              pageCanvas.height = sliceHeight;

              const pageCtx =
                pageCanvas.getContext("2d");

              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(
                0,
                0,
                pageCanvas.width,
                pageCanvas.height
              );

              pageCtx.drawImage(
                canvas,
                0,
                sourceY,
                canvas.width,
                sliceHeight,
                0,
                0,
                canvas.width,
                sliceHeight
              );

              const pageImg =
                pageCanvas.toDataURL("image/png");

              const pageImgHeight =
                sliceHeight * scale;

              if (sourceY > 0) {
                pdf.addPage();
              }

              pdf.addImage(
                pageImg,
                "PNG",
                margin,
                margin,
                imgWidth,
                pageImgHeight
              );

              sourceY = targetY;
            }

            const blob =
              pdf.output("blob");

            const url =
              URL.createObjectURL(blob);

            setVisorConsentimiento({
              url,
              nombre:
                consentimiento.nombreConsentimiento ||
                consentimiento.tipo,
              paciente:
                consentimiento.pacienteNombre ||
                `${paciente?.nombre || ""} ${paciente?.apellidos || ""}`.trim(),
              fecha:
                consentimiento.fecha?.toDate
                  ? consentimiento.fecha
                      .toDate()
                      .toLocaleString("es-MX")
                  : consentimiento.fecha instanceof Date
                    ? consentimiento.fecha.toLocaleString("es-MX")
                    : consentimiento.fecha?.seconds
                      ? new Date(
                          consentimiento.fecha.seconds * 1000
                        ).toLocaleString("es-MX")
                      : consentimiento.fecha || ""
            });

            setModalVisible(false);

          } catch (error) {

            console.error(
              "Error generando visor de consentimiento:",
              error
            );

            mostrarToast(
              "❌ No fue posible abrir el consentimiento"
            );

          }
          finally {
            setConsentimientoAbriendoId(null);
          }
        };

        const imprimirConsentimiento =
          () => {

            if (!pdfRef.current)
              return;

            const ventana =
              window.open(
                "",
                "_blank"
              );

            if (!ventana) {

              mostrarToast(
                "⚠️ Popup bloqueado"
              );

              return;
            }

            ventana.document.write(`
              <html>
                <head>
                  <title>Consentimiento</title>
                </head>
                <body>
                  ${pdfRef.current.innerHTML}
                </body>
              </html>
            `);

            ventana.document.close();

            ventana.focus();

            ventana.print();
        };

        const compartirConsentimiento = async () => {

        if (!consentimientoSeleccionado)
            return;

        try {

            if (navigator.share) {

            await navigator.share({

                title:
                "Consentimiento informado",

                text:
                consentimientoSeleccionado.texto

            });

            } else {

            mostrarToast(
                "⚠️ Compartir no soportado en este dispositivo"
            );
            }

        } catch (err) {

            console.error(err);
        }
        };

  // =========================
  // RENDER
  // =========================

  return (

    <div className="vd-consents-page" style={{
      width:"100%",
      maxWidth:1400,
      display:"flex",
      flexDirection:"column",
      gap:20,
      boxSizing:"border-box"
    }}>

      <section className="vd-consents-hero">
        <div className="vd-consents-hero__main">
          <div className="vd-consents-hero__icon" aria-hidden="true">
            <ShieldCheck size={25} strokeWidth={1.8} />
          </div>
          <div>
            <span className="vd-consents-hero__eyebrow">Expediente legal</span>
            <h2>Consentimientos informados</h2>
            <p>
              Revisa el documento con tu paciente, captura ambas firmas y conserva
              cada versión validada en su expediente.
            </p>
            <span className="vd-consents-hero__patient">
              <UserRound size={13} />
              {`${paciente?.nombre || "Paciente"} ${paciente?.apellidos || ""}`.trim()}
            </span>
          </div>
        </div>
        <div className="vd-consents-hero__stats">
          <div>
            <strong>{consentimientosActivos.length}</strong>
            <span>plantillas activas</span>
          </div>
          <div className="is-saved">
            <strong>{historial.length}</strong>
            <span>firmados</span>
          </div>
          <div className="is-pending">
            <strong>{guardando ? "..." : "Listo"}</strong>
            <span>{guardando ? "guardando" : "para firmar"}</span>
          </div>
        </div>
      </section>

      <div className="vd-consents-steps" aria-label="Flujo de consentimiento">
        <div className="is-active">
          <span>1</span>
          <div><strong>Selecciona</strong><small>la plantilla</small></div>
        </div>
        <i aria-hidden="true" />
        <div className={texto ? "is-active" : ""}>
          <span>2</span>
          <div><strong>Revisa</strong><small>el documento</small></div>
        </div>
        <i aria-hidden="true" />
        <div className={guardando ? "is-active" : ""}>
          <span>3</span>
          <div><strong>Firma y guarda</strong><small>ambas firmas</small></div>
        </div>
      </div>

      {/* TIPO */}

      <div className="vd-consents-template-card" style={{
        background:"#fff",
        padding:20,
        borderRadius:18,
        boxShadow:
          "0 6px 20px rgba(0,0,0,0.06)"
      }}>

        <div className="vd-consents-template-card__heading">
          <div>
            <span className="vd-consents-kicker"><ClipboardCheck size={14} /> Documento a preparar</span>
            <strong>{tipoActual?.nombre || "Selecciona una plantilla"}</strong>
            <small>
              {tipoActual?.origen === "custom"
                ? "Plantilla personalizada de tu consultorio"
                : "Plantilla legal de VexaDent, lista para completar"}
            </small>
          </div>
          <span className="vd-consents-version"><CheckCircle2 size={13} /> v{tipoActual?.version || 1}</span>
        </div>

        <select
          value={tipo}
          onChange={e => {
            setModoHistorial(false);

            const nuevoTipo = e.target.value;

            const configuracionPersonalizada =
              configuracionesConsentimientos.find(
                c =>
                  c.id === nuevoTipo &&
                  c.origen === "custom"
              );

            /*
            * Si es personalizado, generamos inmediatamente
            * el texto desde su plantilla de Firestore.
            */
            if (configuracionPersonalizada) {
              const textoPersonalizado =
                configuracionPersonalizada.contenidoHtml ||
                configuracionPersonalizada.textoPlantilla ||
                "";

              const textoGenerado =
                textoPersonalizado
                  .replace(
                    /\{\{paciente\}\}/g,
                    `${paciente?.nombre || ""} ${paciente?.apellidos || ""}`.trim()
                  )
                  .replace(
                    /\{\{doctor\}\}/g,
                    doctor?.nombre || "Sin configurar"
                  );

              setTexto(textoGenerado);
            }

            setTipo(nuevoTipo);
          }}
          style={{
            width:"100%",
            padding:12,
            borderRadius:12,
            border:
              "1px solid #d1d5db"
          }}
          className="vd-consents-template-select"
        >

        {consentimientosActivos.map(
          consentimiento => (
            <option
              key={consentimiento.id}
              value={consentimiento.id}
            >
              {consentimiento.nombre}
              {consentimiento.origen === "custom"
                ? " — Personalizado"
                : ""}
            </option>
          )
        )}

        </select>

      </div>

      {/* =========================================================
          DOCUMENTO + PANEL LATERAL RESPONSIVE
      ========================================================= */}

      <div
        className="vd-consents-layout"
        style={{
          display: "grid",
          gridTemplateColumns:
            vistaUnaColumna
              ? "1fr"
              : "minmax(0, 3fr) minmax(300px, 380px)",
          gap: 20,
          alignItems: "start",
          width: "100%"
        }}
      >

        {/* =======================================================
            DOCUMENTO
        ======================================================= */}

        <div
          className="vd-consents-document-card"
          style={{
            background: "#fff",
            padding: vistaUnaColumna ? 18 : 24,
            borderRadius: 18,
            boxShadow:
              "0 6px 20px rgba(0,0,0,0.06)",
            minWidth: 0
          }}
        >
          <div className="vd-consents-card-heading">
            <div>
              <span className="vd-consents-kicker"><FileText size={14} /> Documento</span>
              <h2>Consentimiento informado</h2>
              <p>El texto se personaliza con los datos del paciente antes de firmar.</p>
            </div>
            <span className="vd-consents-document-badge"><FileCheck2 size={14} /> Vista previa</span>
          </div>

          <div className="vd-consents-document-meta">
            <span><Sparkles size={13} /> {tipoActual?.nombre || "Sin plantilla seleccionada"}</span>
            <span>Versión {tipoActual?.version || 1}</span>
          </div>

          <h2
            style={{
              display: "none"
            }}
          >
            Consentimiento informado
          </h2>

          <div
            className="vd-consents-document"
            style={{
              width: "100%",
              minHeight:
                vistaUnaColumna
                  ? 420
                  : 620,
              maxHeight:
                vistaUnaColumna
                  ? "65vh"
                  : "72vh",
              borderRadius: 14,
              border:
                "1px solid #d1d5db",
              padding:
                vistaUnaColumna
                  ? "28px"
                  : "40px",
              fontFamily:
                "Times New Roman, serif",
              fontSize:
                vistaUnaColumna
                  ? 15
                  : 16,
              lineHeight: 2,
              overflowWrap:
                "break-word",
              wordBreak:
                "break-word",
              boxSizing:
                "border-box",
              background:
                "#fcfcfc",
              overflowY: "auto",
              position: "relative"
            }}
            ref={pdfRef}
          >
            {renderConsentText(texto)}

            <style>
              {`
                .consentimiento-documento-html {
                  width: 100%;
                }

                .consentimiento-documento-html p {
                  margin: 0 0 18px 0;
                }

                /* LISTAS */

                .consentimiento-documento-html ul,
                .consentimiento-documento-html ol {
                  margin-top: 12px;
                  margin-bottom: 18px;
                  padding-left: 32px;
                }

                .consentimiento-documento-html ul {
                  list-style: none;
                }

                .consentimiento-documento-html ol {
                  list-style: none;
                  counter-reset: consentimiento-item;
                }

                .consentimiento-documento-html ul li,
                .consentimiento-documento-html ol li {
                  position: relative;
                  margin: 0;
                  padding: 0 0 0 4px;
                  line-height: 1.5;
                }

                .consentimiento-documento-html ul li::before {
                  content: "•";
                  position: absolute;
                  left: -22px;
                  top: 0;
                  font-size: 1em;
                  line-height: 1.5;
                }

                .consentimiento-documento-html ol li {
                  counter-increment: consentimiento-item;
                }

                .consentimiento-documento-html ol li::before {
                  content: counter(consentimiento-item) ".";
                  position: absolute;
                  left: -28px;
                  top: 0;
                  width: 24px;
                  text-align: right;
                  font-size: 1em;
                  line-height: 1.5;
                }

                .consentimiento-documento-html ul li > p,
                .consentimiento-documento-html ol li > p {
                  margin: 0;
                  padding: 0;
                  line-height: 1.5;
                }

                /* TABLAS */

                .consentimiento-documento-html table {
                  width: 100%;
                  border-collapse: collapse;
                  table-layout: fixed;
                  margin: 18px 0;
                }

                .consentimiento-documento-html th,
                .consentimiento-documento-html td {
                  border: 1px solid #9ca3af;
                  padding: 8px;
                  vertical-align: top;
                  text-align: left;
                }

                .consentimiento-documento-html th {
                  background: #f3f4f6;
                  font-weight: 700;
                }

                /* LÍNEA DIVISORIA */

                .consentimiento-documento-html hr {
                  border: 0;
                  border-top: 1px solid #9ca3af;
                  margin: 20px 0;
                }

                /* TÍTULOS */

                .consentimiento-documento-html h1,
                .consentimiento-documento-html h2,
                .consentimiento-documento-html h3 {
                  margin-top: 20px;
                  margin-bottom: 12px;
                }
              `}
            </style>
          </div>
        </div>


        {/* =======================================================
            PANEL DERECHO
        ======================================================= */}

        <div
          className="vd-consents-signature-panel"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            minWidth: 0
          }}
        >

          {/* =====================================================
              FIRMA DEL PACIENTE
          ===================================================== */}

          <div
            className="vd-consents-signature-card"
            style={{
              background: "#fff",
              padding: 20,
              borderRadius: 18,
              boxShadow:
                "0 6px 20px rgba(0,0,0,0.06)"
            }}
          >

            <div className="vd-consents-signature-heading"
              style={{
                fontWeight: 700,
                marginBottom: 14,
                fontSize: 16
              }}
            >
              <span className="vd-consents-signature-icon"><PenLine size={16} /></span>
              <span><strong>Firma del paciente</strong><small>El paciente firma en el recuadro</small></span>
            </div>

            <div
              ref={firmaPacienteContainerRef}
              style={{
                border:
                  "1.5px solid #d1d5db",
                borderRadius:12,
                overflow:"hidden",
                background:"#fff",
                width:"100%"
              }}
            >

              <SignatureCanvas
                ref={sigPacienteRef}
                penColor="#111827"
                minWidth={0.6}
                maxWidth={1.8}
                velocityFilterWeight={0.7}
                throttle={8}
                canvasProps={{
                  style: {
                    width: "100%",
                    height:
                      vistaUnaColumna
                        ? "180px"
                        : "220px",
                    background:
                      "#ffffff",
                    touchAction:
                      "none",
                    display:
                      "block",
                    borderRadius:
                      "12px"
                  }
                }}
              />

              </div>

            <div
              style={{
                marginTop: 8,
                fontSize: 13,
                color: "#6b7280"
              }}
            >
              Paciente:
              {" "}
              {paciente?.nombre}
              {" "}
              {paciente?.apellidos}
            </div>

          </div>

          {/* =====================================================
              FIRMA DEL ODONTÓLOGO
          ===================================================== */}

          <div
            className="vd-consents-signature-card"
            style={{
              background: "#fff",
              padding: 20,
              borderRadius: 18,
              boxShadow:
                "0 6px 20px rgba(0,0,0,0.06)",
            }}
          >
            <div className="vd-consents-signature-heading"
              style={{
                fontWeight: 700,
                marginBottom: 14,
                fontSize: 16
              }}
            >
              <span className="vd-consents-signature-icon is-doctor"><ShieldCheck size={16} /></span>
              <span><strong>Firma del odontólogo</strong><small>Valida el documento como profesional</small></span>
            </div>

            <div
              style={{
                border: "1.5px solid #d1d5db",
                borderRadius: 12,
                overflow: "hidden",
                background: "#fff",
                width: "100%"
              }}
            >
              <SignatureCanvas
                ref={sigDoctorRef}
                penColor="#111827"
                minWidth={0.6}
                maxWidth={1.8}
                velocityFilterWeight={0.7}
                throttle={8}
                canvasProps={{
                  style: {
                    width: "100%",
                    height:
                      vistaUnaColumna
                        ? "180px"
                        : "220px",
                    background: "#ffffff",
                    touchAction: "none",
                    display: "block",
                    borderRadius: "12px"
                  }
                }}
              />
            </div>

            <div
              style={{
                marginTop: 8,
                fontSize: 13,
                color: "#6b7280"
              }}
            >
              Odontólogo: {doctor?.nombre || "Sin configurar"}
            </div>
          </div>

          {/* =====================================================
              BOTONES
          ===================================================== */}

          <div className="vd-consents-actions"
            style={{
              display: "flex",
              gap: 10
            }}
          >
            <button
              type="button"
              onClick={limpiarFirmas}
              className="vd-consents-action vd-consents-action--secondary"
              style={{ flex: 1 }}
            >
              <X size={15} /> Limpiar firmas
            </button>

            <button
              type="button"
              onClick={guardar}
              disabled={guardando}
              className="vd-consents-action vd-consents-action--primary"
              style={{ flex: 2 }}
            >
              {guardando ? <><Sparkles size={15} /> Guardando...</> : <><Check size={15} /> Guardar consentimiento</>}
            </button>
          </div>
          </div>


          {/* =====================================================
              HISTORIAL
          ===================================================== */}

          <div
            className="vd-consents-history-card"
            style={{
              background: "#fff",
              padding: 20,
              borderRadius: 18,
              boxShadow:
                "0 6px 20px rgba(0,0,0,0.06)",
              gridColumn:
                vistaUnaColumna
                  ? "1"
                  : "1 / -1"
            }}
          >

            <div className="vd-consents-history-heading"
              style={{
                fontWeight: 700,
                marginBottom: 16,
                fontSize: 17
              }}
            >
              <div>
                <span className="vd-consents-kicker"><History size={14} /> Trazabilidad</span>
                <strong>Historial de consentimientos</strong>
                <small>{ultimoConsentimiento ? "Último documento firmado disponible" : "Aún no hay documentos firmados"}</small>
              </div>
              <span className="vd-consents-history-count">{historial.length}</span>
            </div>

            {historial.length === 0 && (
              <div
                style={{
                  color:
                    "#6b7280",
                  fontSize: 13
                }}
              >
                No hay consentimientos registrados
              </div>
            )}

            {historial.map(c => (
              <div
                className="vd-consents-history-item"
                key={c.id}
                style={{
                  padding: 12,
                  border:
                    "1px solid #e5e7eb",
                  borderRadius: 12,
                  marginBottom: 10,
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  gap: 10
                }}
              >

                <div
                  style={{
                    minWidth: 0
                  }}
                >

                  <div className="vd-consents-history-item__icon"><FileCheck2 size={17} /></div>

                  <div
                    className="vd-consents-history-item__copy"
                    style={{
                      fontWeight: 700,
                      fontSize: 13,
                      overflow: "hidden",
                      textOverflow:
                        "ellipsis",
                      whiteSpace:
                        "nowrap"
                    }}
                  >
                    {c.nombreConsentimiento || c.tipo}
                  </div>

                  <div
                    className="vd-consents-history-item__status"
                    style={{
                      fontSize: 11,
                      color:
                        "#6b7280",
                      marginTop: 4
                    }}
                  >
                    <span><CheckCircle2 size={12} /> Firmado</span>
                    {new Date(
                      c.fecha?.seconds
                        ? c.fecha.seconds * 1000
                        : c.fecha
                    ).toLocaleString()}
                  </div>

                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center"
                  }}
                >

                  <button
                    type="button"
                    onClick={() => {
                      abrirVisorConsentimiento(c);
                    }}
                    className="vd-consents-history-action vd-consents-history-action--view"
                    disabled={
                      consentimientoAbriendoId === c.id
                    }
                    style={{}}
                  >
                    <Eye size={14} />
                    {consentimientoAbriendoId === c.id
                      ? "Abriendo..."
                      : "Ver"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {

                      setConsentimientoAEliminar(c);

                    }}
                    className="vd-consents-history-action vd-consents-history-action--delete"
                    style={{}}
                  >
                    <Trash2 size={14} /> Borrar
                  </button>

                </div>

              </div>
            ))}

          </div>

        </div>

      {/* MODAL CONFIRMAR ELIMINACIÓN */}

      {consentimientoAEliminar && (
        <div
          className="vd-consents-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(0,0,0,0.55)",
            zIndex: 1000000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20
          }}
        >

          <div
            className="vd-consents-modal-card vd-consents-delete-card"
            style={{
              width: "100%",
              maxWidth: 460,
              background: "#fff",
              borderRadius: 20,
              padding: 28,
              boxShadow:
                "0 25px 80px rgba(0,0,0,0.3)"
            }}
          >

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                marginBottom: 18
              }}
            >

              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 14,
                  background:
                    "#fee2e2",
                  color:
                    "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  fontWeight: 800
                }}
              >
                !
              </div>

              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 20
                  }}
                >
                  ¿Eliminar consentimiento?
                </h3>
              </div>

            </div>

            <p
              style={{
                color: "#374151",
                lineHeight: 1.6,
                marginTop: 0
              }}
            >
              Estás a punto de eliminar el
              consentimiento firmado:
            </p>

            <div
              style={{
                background:
                  "#f3f4f6",
                borderRadius: 12,
                padding: 14,
                marginBottom: 18,
                fontWeight: 700
              }}
            >
              {consentimientoAEliminar.tipo}
            </div>

            <p
              style={{
                color: "#4b5563",
                lineHeight: 1.6,
                marginBottom: 8
              }}
            >
              Esta acción eliminará permanentemente
              el consentimiento de VexaDent y de la
              base de datos.
            </p>

            <p
              style={{
                color: "#dc2626",
                fontWeight: 700,
                lineHeight: 1.6,
                marginTop: 0
              }}
            >
              No se puede deshacer ni recuperar
              posteriormente.
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 26
              }}
            >

              <button
                type="button"
                onClick={() =>
                  setConsentimientoAEliminar(null)
                }
                style={{
                  padding:
                    "11px 18px",
                  border:
                    "1px solid #d1d5db",
                  borderRadius: 12,
                  background:
                    "#fff",
                  color:
                    "#374151",
                  cursor:
                    "pointer",
                  fontWeight: 600
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={
                  eliminarConsentimiento
                }
                style={{
                  padding:
                    "11px 18px",
                  border: "none",
                  borderRadius: 12,
                  background:
                    "#dc2626",
                  color: "#fff",
                  cursor:
                    "pointer",
                  fontWeight: 700
                }}
              >
                Sí, eliminar
              </button>

            </div>

          </div>

        </div>
      )}

        {/* MODAL HISTORIAL */}

        {modalVisible &&
        consentimientoSeleccionado && (

        <div
        className="vd-consents-modal-overlay"
        style={{
        position:"fixed",

        inset:0,

        background:
            "rgba(0,0,0,0.55)",

        zIndex:999999,

        display:"flex",

        justifyContent:"center",

        alignItems:"center",

        padding:20

        }}>

        <div
        className="vd-consents-modal-card vd-consents-view-card"
        style={{

            width:"100%",

            maxWidth:1000,

            maxHeight:"90vh",

            overflowY:"auto",

            background:"#fff",

            borderRadius:20,

            padding:28,

            boxShadow:
            "0 25px 80px rgba(0,0,0,0.3)",

            position:"relative"

        }}>

        <button
        onClick={() =>
            setModalVisible(false)
        }
        style={{

            position:"absolute",

            top:18,

            right:18,

            border:"none",

            background:"#ef4444",

            color:"#fff",

            width:40,

            height:40,

            borderRadius:12,

            cursor:"pointer",

            fontWeight:700,

            zIndex:999999

        }}
        >

        ✕

        </button>

        <div
        ref={pdfRef}
        style={{

            background:"#fff",

            padding:"60px 70px",

            color:"#111827",

            fontFamily:
              "Times New Roman, serif",

            width:"100%",

            maxWidth:"760px",

            margin:"0 auto",

            boxSizing:"border-box"

        }}>

            {/* TITULO */}

            <div style={{
            marginBottom:24
            }}>

            <div style={{

                fontSize:40,

                fontWeight:800,

                textAlign:"center",

                marginBottom:8,

                letterSpacing:1

            }}>

                Consentimiento informado

            </div>

            <div style={{

              textAlign:"center",

              marginTop:4,

              marginBottom:18,

              color:"#6b7280",

              fontSize:13,

              letterSpacing:1

            }}>

            Folio:
            {" "}

            {
              consentimientoSeleccionado.folio
            }

            </div>

            <div style={{
                color:"#374151",
                textTransform:"uppercase",
                fontWeight:700,
                textAlign:"center",
                letterSpacing:1,
                marginBottom:20
            }}>
                {
                  consentimientoSeleccionado.nombreConsentimiento ||
                  configuracionesConsentimientos.find(
                    configuracion =>
                      configuracion.id ===
                      consentimientoSeleccionado.tipo
                  )?.nombre ||
                  consentimientoSeleccionado.tipo
                }
            </div>

            </div>

            {/* DOCUMENTO */}

            <div style={{

            border:
                "1px solid #d1d5db",

            borderRadius:12,

            padding:"50px",

            background:"#ffffff",

            lineHeight:1.9,

            fontSize:16,

            fontFamily:
              "Times New Roman, serif",

            textAlign:"justify",

            color:"#111827",

            wordBreak:"break-word"

            }}>

            {consentimientoSeleccionado.texto
              .split("\n")
              .filter(linea => linea.trim() !== "")
              .map((linea, index) => {

                const texto = linea.trim();

                // =========================
                // LISTAS
                // =========================

                if (texto.startsWith("-")) {

                  return (

                    <div
                      key={index}
                      style={{

                        paddingLeft:20,

                        marginBottom:14,

                        lineHeight:1.8,

                        fontSize:16

                      }}
                    >

                      • {texto.replace("-", "").trim()}

                    </div>

                  );
                }

                // =========================
                // TITULOS
                // =========================

                if (
                  texto === texto.toUpperCase() &&
                  texto.length > 8
                ) {

                  return (

                    <div
                      key={index}
                      style={{

                        fontWeight:700,

                        fontSize:18,

                        marginTop:30,

                        marginBottom:18,

                        textAlign:"left",

                        letterSpacing:0.5

                      }}
                    >

                      {texto}

                    </div>

                  );
                }

                // =========================
                // PARRAFOS
                // =========================

                return (

                  <p
                    key={index}
                    style={{

                      marginBottom:22,

                      textAlign:"justify",

                      lineHeight:2,

                      fontSize:16

                    }}
                  >

                    {texto}

                  </p>

                );

              })}

            </div>

            {/* FIRMAS */}

            <div style={{

            display:"grid",

            gridTemplateColumns:
                "1fr 1fr",

            alignItems:"end",

            gap:20,

            marginTop:28

            }}>

            {/* PACIENTE */}

            <div>

                <div style={{
                fontWeight:700,
                marginBottom:10
                }}>

                Firma del paciente

                </div>

                <div style={{

                border:
                    "1px solid #d1d5db",

                borderRadius:14,

                padding:12,

                background:"#ffffff",

                minHeight:180,

                display:"flex",

                alignItems:"center",

                justifyContent:"center"

                }}>

                <img
                  src={
                  consentimientoSeleccionado
                      .firmaPaciente
                  }
                  alt="firma paciente"
                  style={{

                  maxWidth:"80%",

                  maxHeight:120,

                  objectFit:"contain"

                  }}
              />

                </div>

              <div style={{

              marginTop:18,

              borderTop:
                "1px solid #111827",

              paddingTop:10,

              textAlign:"center",

              fontSize:14,

              fontWeight:700

              }}>

              Paciente

              </div>

              </div>

            {/* FECHA */}

            <div style={{

            marginTop:50,

            textAlign:"right",

            color:"#374151",

            fontSize:13,

            fontStyle:"italic",

            borderTop:
              "1px solid #d1d5db",

            paddingTop:18

            }}>

            <div style={{

              marginTop:40,

              display:"inline-block",

              padding:"8px 14px",

              borderRadius:999,

              background:"#dcfce7",

              color:"#166534",

              fontWeight:700,

              fontSize:13,

              letterSpacing:0.5

            }}>

            DOCUMENTO LEGAL VALIDADO

            </div>

            Documento generado y firmado electrónicamente el{" "}

            {new Date(

                consentimientoSeleccionado
                .fecha?.seconds

                ? consentimientoSeleccionado
                    .fecha.seconds * 1000

                : consentimientoSeleccionado
                    .fecha

            ).toLocaleString()}

            </div>
          </div>

            {/* BOTONES */}

            <div
            style={{

            display:"flex",

            gap:12,

            marginTop:28

            }}>

            <button
              onClick={
              imprimirConsentimiento
              }
              style={{

              padding:"12px 18px",

              border:"none",

              borderRadius:14,

              background:"#111827",

              color:"#fff",

              fontWeight:700,

              cursor:"pointer"

              }}
          >

              Imprimir

          </button>

          <button
              onClick={
              descargarPDF
              }
              style={{

              padding:"12px 18px",

              border:"none",

              borderRadius:12,

              background:"#059669",

              color:"#fff",

              fontWeight:700,

              cursor:"pointer"

              }}
          >

              Guardar PDF

          </button>

            <button
                onClick={
                compartirConsentimiento
                }
                style={{

                padding:"12px 18px",

                border:"none",

                borderRadius:12,

                background:"#2563eb",

                color:"#fff",

                fontWeight:700,

                cursor:"pointer"

                }}
            >

                Compartir

            </button>

            </div>

        </div>

        </div>
        </div>
        )}

        {visorConsentimiento && (
          <FileViewerModal
            isOpen={true}
            onClose={() => {
              if (visorConsentimiento.url) {
                URL.revokeObjectURL(
                  visorConsentimiento.url
                );
              }

              setVisorConsentimiento(null);
            }}
            documentType="Consentimiento informado"
            title={
              visorConsentimiento.nombre
            }
            subtitle={
              visorConsentimiento.paciente
            }
            date={
              visorConsentimiento.fecha
            }
            fileUrl={
              visorConsentimiento.url
            }
            filename={
              `${visorConsentimiento.nombre || "Consentimiento"}.pdf`
            }
            mimeType="application/pdf"
            showForward={false}
          />
        )}

        <div
          ref={visorPdfRef}
          style={{
            position: "absolute",
            left: "-10000px",
            top: 0,
            width: "794px",
            background: "#ffffff",
            padding: "40px",
            boxSizing: "border-box",
            color: "#111827",
            fontFamily: "Times New Roman, serif",
            fontSize: "16px",
            lineHeight: 2
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 24,
              marginBottom: 30,
              paddingBottom: 20,
              borderBottom: "1px solid #d1d5db"
            }}
          >
            <div
              style={{
                width: 180,
                flexShrink: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start"
              }}
            >
              {(consentimientoSeleccionado?.clinicSnapshot?.logoBase64 ||
                consentimientoSeleccionado?.clinicSnapshot?.logoURL ||
                clinicData?.logoBase64 ||
                clinicData?.logoURL) && (
                <img
                  src={
                    consentimientoSeleccionado?.clinicSnapshot?.logoBase64 ||
                    consentimientoSeleccionado?.clinicSnapshot?.logoURL ||
                    clinicData?.logoBase64 ||
                    clinicData?.logoURL
                  }
                  alt="Logo del consultorio"
                  style={{
                    maxWidth: "160px",
                    maxHeight: "90px",
                    objectFit: "contain"
                  }}
                />
              )}
            </div>

            <div
              style={{
                flex: 1,
                fontSize: 13,
                lineHeight: 1.6
              }}
            >
              <div
                style={{
                  fontSize: 21,
                  fontWeight: 800,
                  marginBottom: 8
                }}
              >
                {consentimientoSeleccionado?.clinicSnapshot?.nombreClinica ||
                  clinicData?.nombreClinica ||
                  ""}
              </div>

              {(consentimientoSeleccionado?.clinicSnapshot?.direccion ||
                clinicData?.direccion) && (
                <div>
                  <strong>Dirección:</strong>{" "}
                  {[
                    consentimientoSeleccionado?.clinicSnapshot?.direccion?.calle ||
                      clinicData?.direccion?.calle,
                    consentimientoSeleccionado?.clinicSnapshot?.direccion?.numeroExterior ||
                      clinicData?.direccion?.numeroExterior,
                    consentimientoSeleccionado?.clinicSnapshot?.direccion?.colonia ||
                      clinicData?.direccion?.colonia,
                    consentimientoSeleccionado?.clinicSnapshot?.direccion?.municipio ||
                      clinicData?.direccion?.municipio,
                    consentimientoSeleccionado?.clinicSnapshot?.direccion?.estado ||
                      clinicData?.direccion?.estado,
                    consentimientoSeleccionado?.clinicSnapshot?.direccion?.codigoPostal ||
                      clinicData?.direccion?.codigoPostal
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              )}

              {(consentimientoSeleccionado?.clinicSnapshot?.telefono ||
                clinicData?.telefono) && (
                <div>
                  <strong>Teléfono:</strong>{" "}
                  {consentimientoSeleccionado?.clinicSnapshot?.telefono ||
                    clinicData?.telefono}
                </div>
              )}

              {(consentimientoSeleccionado?.clinicSnapshot?.email ||
                clinicData?.email) && (
                <div>
                  <strong>Correo:</strong>{" "}
                  {consentimientoSeleccionado?.clinicSnapshot?.email ||
                    clinicData?.email}
                </div>
              )}

              {(consentimientoSeleccionado?.clinicSnapshot?.RFC ||
                clinicData?.RFC) && (
                <div>
                  <strong>RFC:</strong>{" "}
                  {consentimientoSeleccionado?.clinicSnapshot?.RFC ||
                    clinicData?.RFC}
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              textAlign: "center",
              marginBottom: 30
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                marginBottom: 4
              }}
            >
              CONSENTIMIENTO INFORMADO
            </div>

            <div
              style={{
                fontSize: 14,
                color: "#4b5563"
              }}
            >
              {consentimientoSeleccionado?.nombreConsentimiento ||
                consentimientoSeleccionado?.tipo ||
                ""}
            </div>
          </div>

          <div
            style={{
              marginBottom: 30,
              padding: 16,
              border: "1px solid #d1d5db",
              borderRadius: 10
            }}
          >
            <div>
              <strong>Paciente:</strong>{" "}
              {consentimientoSeleccionado?.pacienteSnapshot?.nombre ||
                consentimientoSeleccionado?.pacienteNombre ||
                ""}{" "}
              {consentimientoSeleccionado?.pacienteSnapshot?.apellidos || ""}
            </div>

            <div>
              <strong>Profesional:</strong>{" "}
              {consentimientoSeleccionado?.doctorSnapshot?.nombre ||
                consentimientoSeleccionado?.doctor?.nombre ||
                ""}
            </div>

            <div>
              <strong>Cédula profesional:</strong>{" "}
              {consentimientoSeleccionado?.doctorSnapshot?.cedula || ""}
            </div>

            <div>
              <strong>Especialidad:</strong>{" "}
              {consentimientoSeleccionado?.doctorSnapshot?.especialidad || ""}
            </div>
          </div>

          {renderConsentText(
            consentimientoSeleccionado?.texto ||
              texto
          )}

          <div
            style={{
              marginTop: 40,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 40
            }}
          >

            {/* FIRMA DEL PACIENTE */}

            {consentimientoSeleccionado?.firmaPaciente && (
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    marginBottom: 10
                  }}
                >
                  Firma del paciente
                </div>

                <img
                  src={
                    consentimientoSeleccionado.firmaPaciente
                  }
                  alt="Firma del paciente"
                  style={{
                    maxWidth: "300px",
                    maxHeight: "120px",
                    objectFit: "contain"
                  }}
                />

                <div
                  style={{
                    marginTop: 10,
                    borderTop: "1px solid #111827",
                    width: "300px",
                    paddingTop: 6,
                    fontSize: 13
                  }}
                >
                  {consentimientoSeleccionado.pacienteNombre ||
                    `${paciente?.nombre || ""} ${paciente?.apellidos || ""}`.trim()}
                </div>
              </div>
            )}

            {/* FIRMA DEL ODONTÓLOGO */}

            {consentimientoSeleccionado?.firmaDoctor && (
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    marginBottom: 10
                  }}
                >
                  Firma del odontólogo
                </div>

                <img
                  src={
                    consentimientoSeleccionado.firmaDoctor
                  }
                  alt="Firma del odontólogo"
                  style={{
                    maxWidth: "300px",
                    maxHeight: "120px",
                    objectFit: "contain"
                  }}
                />

                <div
                  style={{
                    marginTop: 10,
                    borderTop: "1px solid #111827",
                    width: "300px",
                    paddingTop: 6,
                    fontSize: 13
                  }}
                >
                  {consentimientoSeleccionado?.doctorSnapshot?.nombre ||
                    consentimientoSeleccionado?.doctor?.nombre ||
                    ""}
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12
                  }}
                >
                  Cédula profesional:{" "}
                  {consentimientoSeleccionado?.doctorSnapshot?.cedula ||
                    ""}
                </div>
              </div>
            )}

          </div>

          <div
            style={{
              marginTop: 40,
              paddingTop: 20,
              borderTop: "1px solid #d1d5db",
              fontSize: 12,
              lineHeight: 1.65,
              color: "#374151"
            }}
          >
            <div
              style={{
                fontWeight: 800,
                fontSize: 13,
                marginBottom: 10,
                color: "#111827"
              }}
            >
              DECLARACIONES Y CONSIDERACIONES DEL CONSENTIMIENTO INFORMADO
            </div>

            <p>
              Declaro que he recibido información suficiente, clara y comprensible
              respecto del procedimiento clínico bucal autorizado, incluyendo su
              naturaleza, finalidad, objetivos, beneficios esperados, riesgos,
              posibles complicaciones y alternativas de tratamiento disponibles.
            </p>

            <p>
              Manifiesto que tuve oportunidad de realizar preguntas y que mis dudas
              fueron atendidas por el profesional de la salud que proporciona la
              información. Comprendo que los resultados de un procedimiento clínico
              pueden variar de acuerdo con las condiciones particulares de cada
              paciente y que pueden presentarse circunstancias o riesgos imprevistos
              que modifiquen el resultado esperado.
            </p>

            <p>
              Declaro conocer que existen alternativas de tratamiento, incluyendo,
              cuando resulte clínicamente aplicable, la posibilidad de no realizar
              el procedimiento, y manifiesto que la opción elegida corresponde a mi
              decisión libre e informada.
            </p>

            <p>
              Entiendo que puedo revocar mi consentimiento antes del inicio del
              procedimiento, sin perjuicio de las obligaciones que legalmente
              correspondan respecto de actos ya realizados o servicios previamente
              prestados.
            </p>

            <p>
              Se me ha informado que el profesional de la salud procurará en todo
              momento mi bienestar, seguridad y la calidad de los procedimientos
              clínicos realizados, de acuerdo con los principios científicos, éticos
              y profesionales aplicables.
            </p>

            <p>
              Comprendo que este consentimiento no constituye una garantía de un
              resultado determinado y que, cuando las circunstancias clínicas lo
              requieran, podrá ser necesario modificar el plan de tratamiento,
              previa información y consentimiento correspondiente, cuando ello sea
              posible y procedente.
            </p>

            <p>
              Asimismo, autorizo al profesional de la salud para atender las
              contingencias y situaciones de urgencia que pudieran derivarse
              directamente del procedimiento autorizado, dentro de los límites
              establecidos por la legislación sanitaria aplicable y conforme a su
              criterio profesional.
            </p>

            <p>
              La información contenida en este documento forma parte del expediente
              clínico del paciente y deberá ser manejada con confidencialidad,
              privacidad y discreción, de conformidad con las disposiciones jurídicas
              aplicables.
            </p>

            <div style={{ marginTop: 14 }}>
              <strong>Fecha de firma:</strong>{" "}
              {consentimientoSeleccionado?.fecha?.toDate
                ? consentimientoSeleccionado.fecha
                    .toDate()
                    .toLocaleString("es-MX")
                : consentimientoSeleccionado?.fecha instanceof Date
                  ? consentimientoSeleccionado.fecha.toLocaleString("es-MX")
                  : consentimientoSeleccionado?.fecha?.seconds
                    ? new Date(
                        consentimientoSeleccionado.fecha.seconds * 1000
                      ).toLocaleString("es-MX")
                    : consentimientoSeleccionado?.fecha || ""}
            </div>

            <div>
              <strong>Versión del documento:</strong>{" "}
              {consentimientoSeleccionado?.version || 1}
            </div>

            <div>
              <strong>Estado:</strong>{" "}
              {consentimientoSeleccionado?.expedienteLegal?.estado || "firmado"}
            </div>

            <div>
              <strong>Validación:</strong>{" "}
              {consentimientoSeleccionado?.expedienteLegal?.validado
                ? "Documento validado"
                : "Pendiente de validación"}
            </div>

            <div style={{ marginTop: 12 }}>
              Este documento corresponde al consentimiento informado firmado y
              conservado como parte del expediente clínico del paciente.
            </div>
          </div>
        </div>

        {/* TOAST */}

        {toast && (

        <div style={{

            position:"fixed",

            bottom:24,

            right:24,

            background:"#111827",

            color:"#fff",

            padding:"14px 18px",

            borderRadius:12,

            fontWeight:600,

            boxShadow:
            "0 10px 25px rgba(0,0,0,0.18)",

            zIndex:999999

        }}>

            {toast}

        </div>

        )}  
    </div>
  );
}
